import axios from "axios";
import { CustomerGroupImportRejectedRow } from "@/lib/api";

/** Tamanho máximo dos CSVs de importação; a tela lê o arquivo e envia em lotes (csvImportBatches.ts). */
export const MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;
/** Mesmo limite de rejeitadas guardadas no resumo pela API (CustomerGroupService.MaxRejectedInSummary). */
export const MAX_REJECTED_IN_SUMMARY = 5000;
/**
 * Tamanho das rejeitadas em cada parte do resumo da importação de grupos por cliente. Com as linhas do CSV (raw), as
 * 5000 rejeitadas passam do limite de corpo do proxy na frente da API (413); em partes, cada POST fica pequeno.
 */
export const MAX_SUMMARY_PART_BYTES = 256 * 1024;
// Tentativas por requisição antes de parar e oferecer "Retomar"; a espera dobra a cada tentativa (1 s, 2 s).
const MAX_ATTEMPTS = 3;

/** Mensagem do arquivo recusado antes do envio, ou null quando o arquivo pode ir para a API. */
export function validateImportFile(file: File, maxBytes = MAX_IMPORT_FILE_BYTES): string | null {
  if (!file.name.toLowerCase().endsWith(".csv")) return "Selecione um arquivo .csv.";
  if (file.size > maxBytes) return `O arquivo deve ter no máximo ${Math.round(maxBytes / (1024 * 1024))} MB.`;
  if (file.size === 0) return "O arquivo está vazio.";
  return null;
}

/** Sem resposta (rede), 5xx, 408 e 429 valem nova tentativa; 400 (arquivo ou lote inválido) e cancelamento não. */
function isRetryable(error: unknown): boolean {
  if (axios.isCancel(error)) return false;
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === undefined || status >= 500 || status === 408 || status === 429;
}

/** Repete a requisição com espera crescente; signal cancelado interrompe a espera e não tenta de novo. */
export async function withRetry<T>(request: () => Promise<T>, signal?: AbortSignal): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await request();
    } catch (error) {
      if (attempt >= MAX_ATTEMPTS || !isRetryable(error) || signal?.aborted) throw error;
      await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** (attempt - 1)));
      if (signal?.aborted) throw error;
    }
  }
}

/**
 * Divide as rejeitadas em partes de até maxBytes (tamanho do JSON em UTF-8), na ordem em que vieram. Uma linha maior
 * que o limite vai sozinha. Sem rejeitadas, uma parte vazia: o resumo é gravado mesmo assim.
 */
export function splitIntoSummaryParts(
  rows: CustomerGroupImportRejectedRow[],
  maxBytes = MAX_SUMMARY_PART_BYTES,
): CustomerGroupImportRejectedRow[][] {
  const encoder = new TextEncoder();
  const parts: CustomerGroupImportRejectedRow[][] = [];
  let current: CustomerGroupImportRejectedRow[] = [];
  let currentBytes = 0;
  for (const row of rows) {
    // + 1 da vírgula entre os itens do array.
    const bytes = encoder.encode(JSON.stringify(row)).length + 1;
    if (current.length > 0 && currentBytes + bytes > maxBytes) {
      parts.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(row);
    currentBytes += bytes;
  }
  if (current.length > 0 || parts.length === 0) parts.push(current);
  return parts;
}

export function sortByLine<T extends { line: number }>(items: T[]): T[] {
  return [...items].sort((left, right) => left.line - right.line);
}

export function downloadRejected(rows: CustomerGroupImportRejectedRow[], fileName: string) {
  const quote = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
  const content = ["linha;motivo;conteudo", ...rows.map((row) => [row.line, row.reason, row.raw].map(quote).join(";"))].join("\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `rejeitadas-${fileName}`;
  link.click();
  URL.revokeObjectURL(url);
}
